import { useContext, useEffect, useState } from 'react';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { AttachFileButton } from 'atoms/AttachFileButton';
import { postAttachedFile } from 'hooks/u005/usePostAttachedFileHooks';
import { AppContext, AppContextType } from 'templates/AppContextProvider';
import MessageModal from 'organisms/MessageModal';
import { postMypageAttachedFile } from 'hooks/u003/usePostMypageAttachedFileHooks';
import { useParams } from 'react-router-dom';

export const handleFileDownload = async (
  ctx: AppContextType,
  filePath: string | undefined,
  setErrorModal: React.Dispatch<React.SetStateAction<string>>,
  tMessage: (key: string) => string,
  audition_id?: string
) => {
  if (!filePath) return;
  try {
    let res;
    if (audition_id) {
      res = await postAttachedFile(ctx.userId, audition_id, filePath);
    } else {
      res = await postMypageAttachedFile(ctx.userId, filePath);
    }
    if (!res) {
      throw new Error('File download failed');
    }
  } catch (e) {
    console.error('File Download Error:', e);
    setErrorModal(tMessage('MU005F0111E'));
  }
};

const ListItem: React.FC<{
  data: {
    id: number;
    published_at: Date;
    title: string;
    notice?: string;
    file1?: string;
    file2?: string;
    file3?: string;
    file4?: string;
    file5?: string;
  }[];
  err: string | null;
  loading: boolean;
  buttonLabel: string;
  noItemMsg: string;
  initialOpen?: boolean;
}> = (props) => {
  const initialDisplay = 0;
  const [expanded, setExpanded] = useState(props.initialOpen ?? false);
  const [loadDisplay, setLoadDisplay] = useState(initialDisplay);
  const { t } = useTranslationWithLang('mypage');
  const ctx = useContext(AppContext);
  const { t: tMessage } = useTranslationWithLang('message');
  const { t: tCommons } = useTranslationWithLang('commons');
  const [errorModal, setErrorModal] = useState('');

  useEffect(() => {
    if (props.initialOpen && props.data.length > 0 && !props.loading) {
      setExpanded(true);
      setLoadDisplay(props.data.length);
    }
  }, [props.data, props.loading, props.initialOpen]);

  if (props.loading) {
    return <span className="text-[--c-text2] pl-5 text-sm font-bold">Loading...</span>;
  }
  if (props.err) {
    return <span className="text-[--c-error] pl-5 text-sm font-bold">{props.err}</span>;
  }
  if (props.data.length === 0) {
    return <span className="text-[--c-text2] pl-5 text-sm font-bold">{props.noItemMsg}</span>;
  }
  if (!expanded) {
    return (
      <div className="flex justify-between items-center">
        <span className="text-[--c-error] pl-5 text-sm font-bold">
          {t('TU003_006', { news: props.data.length })}
        </span>
        <button
          className="text-[--c-button-text] font-bold py-1 px-4 rounded-full text-xs"
          onClick={() => {
            setExpanded(true);
            setLoadDisplay(props.data.length);
          }}
          style={{ background: 'var(--c-button-background)' }}
          title={props.buttonLabel}
        >
          {props.buttonLabel}
        </button>
      </div>
    );
  }
  return (
    <>
      {errorModal && (
        <MessageModal
          message={errorModal}
          closeLabel={tCommons('TU000_028')}
          onClose={() => setErrorModal('')}
        />
      )}
      <div className="pt-3 pl-5 pb-6 overflow-y-auto relative">
        {props.data.slice(0, loadDisplay).map((d) => (
          <div key={d.id} className="mb-2 max-w-full pr-2">
            <p className="text-[--c-text]">
              {d.published_at
                .toLocaleDateString('ja-JP', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                })
                .replace(/\//g, '.')}
            </p>
            <p className="text-[--c-text2] text-lg break-all">{d.title}</p>
            {d.notice && (
              <p className="text-[--c-text2] ml-1 break-words">
                {d.notice && (
                  <p className="text-[--c-text2] ml-1 break-words whitespace-pre-wrap">
                    {d.notice
                      .split(/(\bhttps?:\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gi)
                      .map((part, i) =>
                        part.match(/^https?:\/\//i) ? (
                          <a
                            key={i}
                            href={part}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline break-all block mt-1"
                            aria-label={part}
                          >
                            {part}
                          </a>
                        ) : (
                          part
                        )
                      )}
                  </p>
                )}
              </p>
            )}
            {d.file1 && (
              <div className="pl-2 text-[--c-text2] break-words whitespace-normal max-w-[200px] flex items-center">
                <AttachFileButton
                  onClick={() => handleFileDownload(ctx, d.file1, setErrorModal, tMessage)}
                  fileNumber={1}
                />
              </div>
            )}
            {d.file2 && (
              <div className="pl-2 text-[--c-text2] break-words whitespace-normal max-w-[200px] flex items-center">
                <AttachFileButton
                  onClick={() => handleFileDownload(ctx, d.file2, setErrorModal, tMessage)}
                  fileNumber={2}
                />
              </div>
            )}
            {d.file3 && (
              <div className="pl-2 text-[--c-text2] break-words whitespace-normal max-w-[200px] flex items-center">
                <AttachFileButton
                  onClick={() => handleFileDownload(ctx, d.file3, setErrorModal, tMessage)}
                  fileNumber={3}
                />
              </div>
            )}
            {d.file4 && (
              <div className="pl-2 text-[--c-text2] break-words whitespace-normal max-w-[200px] flex items-center">
                <AttachFileButton
                  onClick={() => handleFileDownload(ctx, d.file4, setErrorModal, tMessage)}
                  fileNumber={4}
                />
              </div>
            )}
            {d.file5 && (
              <div className="pl-2 text-[--c-text2] break-words whitespace-normal max-w-[200px] flex items-center">
                <AttachFileButton
                  onClick={() => handleFileDownload(ctx, d.file5, setErrorModal, tMessage)}
                  fileNumber={5}
                />
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="absolute bottom-[0.01rem] right-2">
        <button
          className="text-[--c-button-text] font-bold py-1 px-4 rounded-full text-xs"
          onClick={() => {
            setExpanded(!expanded);
            setLoadDisplay(expanded ? initialDisplay : props.data.length);
          }}
          style={{ background: 'var(--c-button-background)' }}
          title={t('TU003_007')}
        >
          {t('TU003_007')}
        </button>
      </div>
    </>
  );
};

export const NotificationArea: React.FC<{
  title: string;
  icon: React.ReactNode;
  data: {
    id: number;
    published_at: Date;
    title: string;
    notice?: string;
    file1?: string;
    file2?: string;
    file3?: string;
    file4?: string;
    file5?: string;
  }[];
  err: string | null;
  loading: boolean;
  expanded: boolean;
  noItemsMsg: string;
  buttonLabel: string;
  isSp: boolean;
  initialOpen?: boolean;
}> = (props) => {
  const isSp = props.isSp;
  const ctx = useContext(AppContext);
  const { t: tMessage } = useTranslationWithLang('message');
  const { t: tCommons } = useTranslationWithLang('commons');
  const [errorModal, setErrorModal] = useState('');
  const routeParams = useParams();
  const { audition_id } = routeParams;

  if (isSp) {
    return (
      <div className="rounded-lg w-full bg-[--c-background2] mt-3 mb-3 pt-2 pr-2 pb-2 items-center transition-[max-height]">
        <div className="relative">
          <div className="absolute top-[-4px] left-[-15px]">
            <div
              className="w-[30px] h-[30px] rounded-full flex justify-center items-center"
              style={{ background: 'var(--c-secondary)' }}
            >
              {props.icon}
            </div>
          </div>
          <div className="flex flex-col">
            <ListItem
              data={props.data}
              err={props.err}
              loading={props.loading}
              buttonLabel={props.buttonLabel}
              noItemMsg={props.noItemsMsg}
              initialOpen={props.initialOpen}
            />
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      {errorModal && (
        <MessageModal
          message={errorModal}
          closeLabel={tCommons('TU000_028')}
          onClose={() => setErrorModal('')}
        />
      )}
      <div className="w-full flex justify-center h-full">
        <div className="rounded-lg w-full bg-[--c-background2] pt-2 pr-2 pb-2 h-full overflow-hidden">
          <div className="relative h-full">
            <div className="flex flex-col grow w-auto h-full">
              <div className="flex justify-center">
                <div
                  className="w-[30px] h-[30px] rounded-full flex justify-center items-center"
                  style={{ background: 'var(--c-secondary)' }}
                >
                  {props.icon}
                </div>
                <h2 className="text-[--c-textinput-label] font-bold pl-[10px]">{props.title}</h2>
              </div>
              <div className="flex flex-col h-full pb-7">
                {props.loading && (
                  <span className="text-[--c-text2] pl-5 text-sm font-bold">Loading...</span>
                )}
                {!props.loading && props.err && (
                  <span className="text-[--c-error] pl-5 text-sm font-bold">{props.err}</span>
                )}
                {!props.err && !props.loading && props.data.length === 0 && (
                  <span className="text-[--c-text2] pl-5 text-sm font-bold">
                    {props.noItemsMsg}
                  </span>
                )}
                <ul className="flex flex-col h-full transition-all pt-3 pl-5 pb-3 overflow-y-auto">
                  {!props.loading &&
                    props.data.map((d) => (
                      <li key={d.id} className="mb-2">
                        <p className="text-[--c-text]">
                          {d.published_at
                            .toLocaleDateString('ja-JP', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                            })
                            .replace(/\//g, '.')}
                        </p>
                        <p className="text-[--c-text2] text-lg break-all">{d.title}</p>
                        {d.notice && (
                          <p className="text-[--c-text2] ml-1 break-words whitespace-pre-wrap">
                            {d.notice
                              .split(
                                /(\bhttps?:\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gi
                              )
                              .map((part, i) =>
                                part.match(/^https?:\/\//i) ? (
                                  <a
                                    key={i}
                                    href={part}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline break-all block mt-1"
                                    aria-label={part}
                                  >
                                    {part}
                                  </a>
                                ) : (
                                  part
                                )
                              )}
                          </p>
                        )}
                        {d.file1 && (
                          <div className="pl-2 text-[--c-text2] break-words whitespace-normal max-w-[200px] flex items-center">
                            <AttachFileButton
                              onClick={() =>
                                handleFileDownload(
                                  ctx,
                                  d.file1,
                                  setErrorModal,
                                  tMessage,
                                  audition_id
                                )
                              }
                              fileNumber={1}
                            />
                          </div>
                        )}
                        {d.file2 && (
                          <div className="pl-2 text-[--c-text2] break-words whitespace-normal max-w-[200px] flex items-center">
                            <AttachFileButton
                              onClick={() =>
                                handleFileDownload(
                                  ctx,
                                  d.file2,
                                  setErrorModal,
                                  tMessage,
                                  audition_id
                                )
                              }
                              fileNumber={2}
                            />
                          </div>
                        )}
                        {d.file3 && (
                          <div className="pl-2 text-[--c-text2] break-words whitespace-normal max-w-[200px] flex items-center">
                            <AttachFileButton
                              onClick={() =>
                                handleFileDownload(
                                  ctx,
                                  d.file3,
                                  setErrorModal,
                                  tMessage,
                                  audition_id
                                )
                              }
                              fileNumber={3}
                            />
                          </div>
                        )}
                        {d.file4 && (
                          <div className="pl-2 text-[--c-text2] break-words whitespace-normal max-w-[200px] flex items-center">
                            <AttachFileButton
                              onClick={() =>
                                handleFileDownload(
                                  ctx,
                                  d.file4,
                                  setErrorModal,
                                  tMessage,
                                  audition_id
                                )
                              }
                              fileNumber={4}
                            />
                          </div>
                        )}
                        {d.file5 && (
                          <div className="pl-2 text-[--c-text2] break-words whitespace-normal max-w-[200px] flex items-center">
                            <AttachFileButton
                              onClick={() =>
                                handleFileDownload(
                                  ctx,
                                  d.file5,
                                  setErrorModal,
                                  tMessage,
                                  audition_id
                                )
                              }
                              fileNumber={5}
                            />
                          </div>
                        )}
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
